import React, { useState, useCallback, useEffect } from "react";
import { useHistory } from "react-router-dom"; 
import { Menu, Icon, Image, Dropdown, Transition, Accordion } from "semantic-ui-react";
import { createErrorResponse, createFile, handleErrorResponse, handleSpaFwEventLog, isValidPermission, isWhiteLabeled } from "../Helpers";
import { getMockTenantLogo, patchMockUser } from "../api/mock/MockGlobalApi";
import { getTenantLogo, patchUser } from "../api/GlobalApi";
import BCWordLogo from "../assets/bc-wordmark.svg";
import BCLogo from "../assets/bc-mark.svg";
import BCDefaultAvatar from "../assets/bc-avatar-builder.svg";
import { useMsal } from "@azure/msal-react";
import "../styles/main.scss";
import "../styles/navbar.scss";
import { handleApiPromise, logout } from "../helpers/GlobalHelper";
import { FeatureFlags } from "./constants/LocalServerConstants";
import { GlobalConstants, Personas } from "./constants/GlobalConstants";
import { whoAmI } from "../helpers/PermissionHelper";

const BCNavbar = ({
  isGreaterThanMobile, 
  isSidebar, 
  setIsSidebar, 
  isSidebarToggle,
  setIsMessage,
  setErrorResponse,
  user, 
  proxyUser,
  removeCookie,
  portalPermissionsList,
  completedW9,
  isProfileLoading,
  permissions,
  profile
}) => {
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [activeAccordions, setActiveAccordions] = React.useState([] as number[]);
  const [tenantLogo, setTenantLogo] = React.useState(undefined as any);
  const { instance } = useMsal();
  const history = useHistory();
  const userPersona = whoAmI(user);
  const handleProfile = useCallback(() => {
    history.push("/profile");
  }, [history]);

  const handleProxy = useCallback(() => {
    history.push("/proxy");
  }, [history]);

  const handleFeatureFlags = useCallback(() => {
    history.push("/featureflags");
  }, [history]);

  const handleHome = useCallback(() => {
    history.push("/");
  }, [history]);

  const handleLogout = async () => {
    if (user?.preferences?.proxyId) {
      const preferences = user?.preferences || {};
      delete preferences.proxyId;
      const userPatchObj = {
        id: user.profileId,
        preferences: JSON.stringify(preferences)
      };

      await handleApiPromise(
        () => patchUser(userPatchObj),
        () => patchMockUser(userPatchObj),
        (response) => handleErrorResponse(response, setIsMessage, setErrorResponse),
        false
      ) || false;
    }

    logout(instance, removeCookie);
  };

  const handlePortal = async (portal) => {
    setIsProfileOpen(false);
    if (user && user.profileId) {
      const userPatchObj = {
        id: user.profileId,
        preferences: JSON.stringify({ portal: portal })
      };

      await patchUserAsync(userPatchObj);
      handleHome();
      window.location.reload();
    }
  };

  const handleActiveAccordions = (id) => {
    const foundAccordionIndex = activeAccordions.indexOf(id);
    if (foundAccordionIndex > -1) {
      activeAccordions.splice(foundAccordionIndex, 1);
    }
    else {
      activeAccordions.push(id);
    }
    setActiveAccordions([...activeAccordions]);
  };

  const patchUserAsync = async (patchObj) => {
    try {
      const response = FeatureFlags.IS_MOCK ? await patchMockUser(patchObj) : await patchUser(patchObj);
      if (response && response.ok) {
        return true;
      }
      else {
        await handleErrorResponse(response, setIsMessage, setErrorResponse);
      }
    }
    catch (error: any) {
      handleSpaFwEventLog("Caught error while trying to patch user", setIsMessage, setErrorResponse);  
    }

    return false;
  };
  
  const handleGetTenantLogo = useCallback(async () => {
    try {
      const response = FeatureFlags.IS_MOCK ? await getMockTenantLogo() : await getTenantLogo(proxyUser.id) as any;
      if (response && response.ok) {
        const data = await response.blob();
        const tenantLogo = await createFile(data, "profile-avatar", data.type);
        setTenantLogo(URL.createObjectURL(tenantLogo));
      }
      else {
        createErrorResponse("ErrorResponse", "Caught Error while trying to get tenant logo", undefined , "user/tenant-logo", undefined, undefined, "GET", response);
      }
    }
    catch (error: any) {
      createErrorResponse("Error", "Caught Error while trying to get tenant logo", undefined , "user/tenant-logo", undefined, error.status, "GET", error);
    }
  }, [proxyUser]);

  useEffect(() => {
    isWhiteLabeled(permissions) && handleGetTenantLogo();
  }, [handleGetTenantLogo, permissions]);

  let timeoutId: any = null;

  const handleGetOrganizationText = () => {
    if (proxyUser && proxyUser.id) {
      profile = proxyUser;
    }
    
    if (profile && profile.persona && profile.persona === Personas.BROKER) {
      if (profile.organization && !profile.organization.useWhitelabelPreferences) {
        return <div className="navbar-profile-portal">builderscapital.serviceyourloan.com</div>;
      }
      else if (profile.organization && profile.organization.subdomain) {
        return <div className="navbar-profile-portal">{`${profile.organization.subdomain}.serviceyourloan.com`}</div>;
      }
      else if (profile.organization && !profile.organization.subdomain) {
        return <div className="navbar-profile-portal">portal.serviceyourloan.com</div>;
      }
    }
    else if (profile && profile.isExternalUser === false && profile.organization && profile.organization.name) {
      return <div className="navbar-profile-portal">{profile.organization.name}</div>;
    }
    
    return null;
  };

  const handleProfileMouseOver = () => {
    clearTimeout(timeoutId);
    setIsProfileOpen(true);
  };

  const handleProfileMouseOut = () => {
    timeoutId = setTimeout(() => {
      setIsProfileOpen(false);
    }, 300);
  };

  return (
    <div>
      {<Menu attached="top" borderless className="navbar-menu">
        {isSidebarToggle && <Icon 
          className="navbar-collapse-icon vertical-center cursor-pointer" 
          name='content' 
          size="big"
          onClick={() => setIsSidebar(!isSidebar)}
        />}
        {(isWhiteLabeled(permissions)) ?
          <Image src={tenantLogo} /> : isValidPermission(permissions, "BrokerPortal", "read") ?
          <Image className={isGreaterThanMobile ? "navbar-word-logo" : "navbar-logo"} src={isGreaterThanMobile ? BCWordLogo : BCLogo} /> : null
        }
        {!isProfileLoading && <Menu.Menu className="navbar-avatar-container" position="right" onMouseOver={handleProfileMouseOver} onMouseOut={handleProfileMouseOut}>
          <div>
            {isValidPermission(permissions, "BrokerW9", "read") && !completedW9 && <div className="navbar-profile-alert">
              <Icon className="navbar-profile-alert-icon" name="exclamation circle" color="red" />
            </div>}
            {user && user.avatar ? <Image className="navbar-avatar vertical-center" src={user.avatar} onClick={isGreaterThanMobile && isValidPermission(permissions, "BrokerPortal", "read") ? handleProfile : void 0} /> : 
            <div className="navbar-avatar vertical-center">
              <Image className="navbar-avatar-default vertical-center" src={BCDefaultAvatar} onClick={isGreaterThanMobile && isValidPermission(permissions, "BrokerPortal", "read") ? handleProfile : void 0} />
            </div>}
          </div>
          {isGreaterThanMobile && <div className="navbar-profile-text-container" onClick={isValidPermission(permissions, "BrokerPortal", "read") ? handleProfile : void 0}>
            <div className="navbar-profile-name">{user && (isValidPermission(permissions, "GlobalAdminAccess", "read") && proxyUser.id !== undefined) ? user.name + ` Proxied as ${proxyUser.firstName} ${proxyUser.lastName}` : user.name}{localStorage.getItem("beta") && " (BETA)"}</div>
            <div className="navbar-profile-portal">{handleGetOrganizationText()}</div>
          </div>}
          <Transition.Group animation={"fade down"} duration={{hide: 300, show: 150}}>
            {isProfileOpen && <Dropdown open={isProfileOpen} icon={null} className="navbar-profile-dropdown-container">
              <Dropdown.Menu className={isGreaterThanMobile ? "navbar-profile-dropdown" : "navbar-profile-dropdown-mobile"}>
                {isValidPermission(permissions, "BrokerPortal", "read") && <Dropdown.Item className="navbar-profile-item" onClick={handleProfile}><Icon name='user' size='mini' />Profile
                {isValidPermission(permissions, "BrokerW9", "read") && !completedW9 && <Icon className="navbar-profile-alert-icon" name="exclamation circle" color="red" />}</Dropdown.Item>}
                {userPersona === Personas.SYSTEM_ADMIN && <Dropdown.Item className="navbar-profile-item" onClick={handleProxy}><Icon name='user secret' size='mini' />Proxy</Dropdown.Item>}
                {isValidPermission(permissions, "FeatureFlag", "read") && <Dropdown.Item className="navbar-profile-item" onClick={() => handleFeatureFlags()}><Icon name='bold' size='mini' />Feature Flags</Dropdown.Item>}
                <Dropdown.Item className="navbar-profile-item" onClick={() => handleLogout()}><Icon name='sign out' size='mini' />Sign Out</Dropdown.Item>
                {(portalPermissionsList && portalPermissionsList.length > 1) && <React.Fragment>
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={() => handleActiveAccordions(0)}>
                  <Accordion>
                    <Accordion.Title
                      className="navbar-profile-accordion"
                      active={activeAccordions.includes(0)}
                      index={0}
                      content={<div className="navbar-profile-accordion-title">
                        <Icon name='sign out' size='mini' />
                        <div>Portals</div>
                      </div>}
                    />
                    <Accordion.Content active={activeAccordions.includes(0)}>
                      {portalPermissionsList.includes(GlobalConstants.Portals.BROKER) && <div className="navbar-profile-accordion-item" onClick={() => handlePortal(GlobalConstants.Portals.BROKER)}>The Portals</div>}
                      {portalPermissionsList.includes(GlobalConstants.Portals.LMS) && <div className="navbar-profile-accordion-item" onClick={() => handlePortal(GlobalConstants.Portals.LMS)}>{GlobalConstants.Portals.LMS}</div>}
                      {portalPermissionsList.includes(GlobalConstants.Portals.BORROWER) && <div className="navbar-profile-accordion-item" onClick={() => handlePortal(GlobalConstants.Portals.BORROWER)}>Servicing</div>}
                      {portalPermissionsList.includes(GlobalConstants.Portals.LOAN_OFFICER) && <div className="navbar-profile-accordion-item" onClick={() => handlePortal(GlobalConstants.Portals.LOAN_OFFICER)}>{GlobalConstants.Portals.LOAN_OFFICER}</div>}
                    </Accordion.Content>
                  </Accordion>
                  </Dropdown.Item>
                </React.Fragment>}
              </Dropdown.Menu>
            </Dropdown>}
          </Transition.Group>
        </Menu.Menu>}
      </Menu>}
    </div>
  );
};

export default BCNavbar;
